import { Conditions, LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Account } from '@/client/accounts';

import {
  Campaign,
  CampaignActionsEnum,
  CampaignStatusesEnum,
  CampaignStatusMessages,
  CampaignTemplate,
  CampaignType,
  ListCampaignsResponse,
  SendTestEmailFormValues,
} from '@/client/campaigns/types';

import { FilterNamesEnum, getFiltersFromColumns } from '@/client/helpers';
import { Actions, Subjects, UserCampaignType } from '@/client/users';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import {
  CampaignTemplatesModal,
  SendTestEmailModal,
} from '@/components/campaigns';
import {
  DataTable,
  DataTableActions,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { useNotifications } from '@/hooks/notifications.hook';
import {
  useCampaignAction,
  useCampaigns,
  useCampaignsSummary,
  useCopyCampaign,
  useDeleteCampaign,
  useSaveCampaign,
  useSendTestEmail,
} from '@/hooks/query';
import {
  useCampaignTemplates,
  useCopyCampaignTemplate,
  useDeleteCampaignTemplate,
  useSaveCampaignTemplate,
} from '@/hooks/query/campaign-templates.hook';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUserState } from '@/store/features/users';
import { DateFormats, DialogType } from '@/system-settings/enums';
import { AppButton, AppDropdownButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FormatDate } from '@/ui/date';
import { ImageCard } from '@/ui/image-card';
import { AppSelectPreviewType } from '@/ui/select-preview';
import { CardGridContainer, FlexContainer } from '@/ui/styled-ui';
import { TabSwitchButtons } from '@/ui/tab-switch-buttons';
import {
  displayCampaignStatus,
  displayCreatedBy,
  FalsePositiveIcon,
  getCampaignStatusOptions,
  getPhishingSignOptions,
  isDemoRestrictedMode,
  PreviewType,
  queryStateConverter,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import {
  default as React,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { default as styled } from 'styled-components';
import { GridTable } from '../grid-table';
import { DataTableFilters, DataTableToolbar } from '../tables/crud';
import { ThumbnailsGenerationNotify } from '../thumbnails';

const StyledDiv = styled.div`
  width: 100%;
  padding-top: var(--default-padding);

  .p-datatable {
    th {
      &.email-sent-icon .p-column-title:before,
      &.users-icon .p-column-title:before,
      &.email-opened-icon .p-column-title:before,
      &.clicked-link-icon .p-column-title:before,
      &.submitted-data-icon .p-column-title:before,
      &.email-reported-icon .p-column-title:before {
        font-family: var(--icon-font-family);
        display: inline-block;
        color: var(--black-main);
        font-size: var(--medium-font-size);
        line-height: var(--medium-line-height);
        padding-right: 2px;
      }

      &.email-sent-icon .p-column-title:before {
        font-weight: 500;
        content: '\f0e0';
      }

      &.users-icon .p-column-title:before {
        font-weight: 500;
        content: '\f007';
      }

      &.email-opened-icon .p-column-title:before {
        font-weight: 500;
        content: '\f2b6';
      }

      &.clicked-link-icon .p-column-title:before {
        font-weight: 900;
        content: '\f245';
      }

      &.submitted-data-icon .p-column-title:before {
        font-weight: 900;
        content: '\f06a';
      }

      &.email-reported-icon .p-column-title:before {
        font-weight: 900;
        content: '\f0a1';
      }

      &.false-positive-icon .p-column-header-content {
        justify-content: center;
      }

      &.p-highlight .p-column-title:before {
        color: var(--red-main);
      }
    }
  }
`;

const StyledCardGridViewContainer = styled(CardGridContainer)`
  margin: 0;
  width: 100%;
  @media screen and (max-width: 1440px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media screen and (max-width: 1080px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (max-width: 800px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const StyledText = styled.div`
  color: var(--gray-darker);
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-heigth);
  font-style: italic;
  min-width: max-content;
`;

const StyledLink = styled.a`
  color: var(--black-main);
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledTabs = styled(TabSwitchButtons)`
  align-self: flex-start;
  min-width: max-content;
  flex: 0 1 10%;
`;

const getStatus = (
  status: CampaignStatusesEnum,
  isDynamic: boolean,
  t: TranslationFunctionType,
) => {
  return (
    <>
      <div>{t(CampaignStatusMessages[status])}</div>
      {isDynamic && <StyledText>({t('campaign.dynamicTarget')})</StyledText>}
    </>
  );
};

type CampaignListProps = {
  showIndividualCampaigns?: boolean;
  onShowIndividualCampaignsChange?: (value: boolean) => void;
};

export const CampaignList = ({
  showIndividualCampaigns: controlledShowIndividual,
  onShowIndividualCampaignsChange,
}: CampaignListProps = {}) => {
  const { can, cannot } = usePermission();
  const { canUseFeature } = useFeatureFlag();
  const account = useAppSelector(selectCurrentAccount);
  const { user } = useAppSelector(selectCurrentUserState);
  const isDemoAccount = isDemoRestrictedMode(account, user);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const [selectedPreviewType, setSelectedPreviewType] = useState<PreviewType>(
    PreviewType.TABLE,
  );
  const [internalShowIndividual, setInternalShowIndividual] = useState(false);
  const showIndividualCampaigns =
    controlledShowIndividual ?? internalShowIndividual;
  const setShowIndividualCampaigns =
    onShowIndividualCampaignsChange ?? setInternalShowIndividual;

  const languageValueToKey = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(LanguagesEnum).map(([key, value]) => [value, key]),
      ),
    [],
  );

  const isTable = selectedPreviewType === PreviewType.TABLE;

  const {
    skip,
    sort,
    take,
    apiFilters,
    onSort,
    onFilter,
    onPage,
    onGridPageChange,
    onGridRowChange,
  } = useTable({
    tableName: account?.isSystem
      ? TableNamesEnum.CAMPAIGNS_LIST_SYSTEM
      : TableNamesEnum.CAMPAIGNS_LIST_CUSTOMER,
  });

  const commaSeparatedSort = sort && sort.length > 0 ? [sort.join(',')] : [];

  const [data, setData] = useState<ListCampaignsResponse>();

  const {
    isLoading: areCampaignsLoading,
    campaigns,
    refetch,
  } = useCampaigns({
    showIndividualCampaigns: showIndividualCampaigns ? 1 : 0,
    withTargets: showIndividualCampaigns ? 1 : 0,
    withTemplates: isTable ? 0 : 1,
    take,
    skip,
    enabled: !account?.isSystem,
    filters: [
      ...apiFilters,
      {
        field: 'account',
        condition: Conditions.EQUAL,
        value: (account as Account)?.id,
      },
    ],
    sort: commaSeparatedSort,
  });

  const {
    isLoading: areCampaignTemplatesLoading,
    campaignTemplates,
    refetch: refetchCampaignTemplates,
  } = useCampaignTemplates({
    withPreview: isTable ? 0 : 1,
    take,
    skip,
    enabled: account?.isSystem,
    filters: [...apiFilters],
    sort: commaSeparatedSort,
    sortNulls: commaSeparatedSort?.includes('isPublished,asc')
      ? 'first'
      : 'last',
  });

  useEffect(() => {
    if (account?.isSystem && !areCampaignTemplatesLoading) {
      setData(campaignTemplates);
    } else if (!account?.isSystem && !areCampaignsLoading) {
      setData(campaigns);
    }
  }, [campaigns, campaignTemplates]);

  useEffect(() => {
    refetch();
  }, [showIndividualCampaigns]);

  const {
    lastMessage,
    setNotificationParam: setCampaignId,
    notificationParam: notificationCampaignId,
    isConnected,
  } = useNotifications(client.campaigns.campaignsNotifyUrl);

  const [copyState, setCopyState] = useState<LoadingStatuses>(
    LoadingStatuses.IDLE,
  );
  const [showCampaignTemplatesModal, setShowCampaignTemplatesModal] =
    useState(false);

  const [deletingCampaignId, setDeletingCampaignId] = useState<string | null>(
    null,
  );

  const [showSendTestEmailForACampaing, setShowSendTestEmailForACampaing] =
    useState<Campaign | null>(null);

  const deleteCampaign = useDeleteCampaign();
  const deleteCampaignTemplate = useDeleteCampaignTemplate();

  const { refetch: summaryRefetch } = useCampaignsSummary({
    accountId: account?.id,
    showIndividualCampaigns: showIndividualCampaigns ? 1 : 0,
  });
  const campaignAction = useCampaignAction();

  useEffect(() => {
    if (!notificationCampaignId) return;
    handleNotifyCampaignComplete(lastMessage);
  }, [JSON.stringify(lastMessage)]);

  const handleNotifyCampaignComplete = async (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'campaign.complete.sync.finished',
        'campaign.complete.sync.failed',
      ].includes(messages?.data?.event)
    )
      return;

    if (messages?.data?.event === 'campaign.complete.sync.finished') {
      await refetch();
      await summaryRefetch();

      toast?.success(t('toast.success'), t('campaign.completed'));
    }

    if (messages?.data?.event === 'campaign.complete.sync.failed') {
      toast?.success(t('toast.success'), t('campaign.not.completed'));
    }

    setCampaignId(undefined);
  };

  const {
    lastMessage: lastMessageDeletion,
    setNotificationParam: setIdForDeletion,
    notificationParam: notificationForDeletion,
    isConnected: isConnectedForDeletion,
  } = useNotifications(client.campaigns.campaignsNotifyUrl);

  useEffect(() => {
    if (!notificationForDeletion) return;
    handleNotifyCampaignDelete(lastMessageDeletion);
  }, [JSON.stringify(lastMessageDeletion)]);

  const handleNotifyCampaignDelete = async (messages: any) => {
    if (
      !messages?.data?.event ||
      !['campaign.delete.finished', 'campaign.delete.error'].includes(
        messages?.data?.event,
      )
    )
      return;

    if (messages?.data?.event === 'campaign.delete.finished') {
      setDeletingCampaignId(null);
      toast?.success(t('toast.success'), t('campaign.deleted'));
      await refetch();
    }

    if (messages?.data?.event === 'campaign.delete.error') {
      setDeletingCampaignId(null);
      toast?.error(t('toast.error'), t('campaign.not.deleted'), 5000);
      await refetch();
    }

    setIdForDeletion(undefined);
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    setDeletingCampaignId(campaignId);
    try {
      const service = account?.isSystem
        ? deleteCampaignTemplate
        : deleteCampaign;
      await service.delete(campaignId);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setDeletingCampaignId(null);
    }
  };

  const sendTestEmail = useSendTestEmail();
  const {
    lastMessage: testMailLastMessage,
    setNotificationParam: setJobId,
    notificationParam: jobId,
  } = useNotifications(client.senderProfiles.senderProfilesJobNotifyUrl);

  useEffect(() => {
    if (!jobId) return;
    handleNotifyEmailSent(testMailLastMessage);
  }, [JSON.stringify(testMailLastMessage)]);

  const handleNotifyEmailSent = (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'sending-profile.email-sent',
        'sending-profile.email-not-sent',
      ].includes(messages?.data?.event)
    )
      return;

    if (messages?.data?.event === 'sending-profile.email-sent')
      toast?.success(t('toast.success'), t('campaign.testEmail.sent'), 5000);
    if (messages?.data?.event === 'sending-profile.email-not-sent')
      toast?.info(t('toast.info'), t('campaign.testEmail.notSent'), 5000);

    setJobId(undefined);
  };

  const handleSendTestEmail = async (data: SendTestEmailFormValues) => {
    if (!showSendTestEmailForACampaing?.id) return;

    try {
      const response = await sendTestEmail.send({
        email:
          data.emailType === 'custom'
            ? data.email || user?.email || ''
            : (data.admin?.email as string),
        firstName:
          data.emailType === 'custom' ? data.firstName : data.admin?.firstName,
        lastName:
          data.emailType === 'custom' ? data.lastName : data.admin?.lastName,
        position: data.emailType === 'custom' ? data.position : undefined,
        campaignId: showSendTestEmailForACampaing.id,
        emailTemplateId: showSendTestEmailForACampaing?.emailTemplate?.id,
        sendingProfileId: showSendTestEmailForACampaing?.sendingProfile?.id,
        landingPageId: showSendTestEmailForACampaing?.landingPage?.id,
        encodeEmailTemplateImages:
          showSendTestEmailForACampaing?.meta?.encodeEmailTemplateImages,
      });

      setJobId(response.jobId);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }

    setShowSendTestEmailForACampaing(null);
  };

  const copyCampaign = useCopyCampaign();
  const copyCampaignTemplate = useCopyCampaignTemplate();
  const handleCopyCampaign = async (campaign: Campaign) => {
    try {
      setCopyState(LoadingStatuses.LOADING);
      const service = account?.isSystem ? copyCampaignTemplate : copyCampaign;

      const response = await service.copy({
        campaignId: campaign?.id,
        account: (account as Account)?.id,
      });
      setCopyState(LoadingStatuses.IDLE);

      if (response && !response?.id) return;

      navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](response?.id), {
        state: { isTemplate: !!account?.isSystem },
      });
      toast?.success(
        t('toast.success'),
        account?.isSystem
          ? t('campaign.template.copied')
          : t('campaign.copied'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const saveCampaign = useSaveCampaign();
  const saveCampaignTemplate = useSaveCampaignTemplate();

  const handleCreateNewCampaign = async ({
    isTemplate,
  }: {
    isTemplate?: boolean;
  }) => {
    try {
      if (isDemoAccount) return;

      const service = isTemplate ? saveCampaignTemplate : saveCampaign;
      const response = await service.create({
        name: isTemplate ? t('campaign.new.template') : t('campaign.new'),
        account: account?.id,
        encodeEmailTemplateImages: true,
      });

      if (!response?.id) return;

      navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](response?.id), {
        state: { isTemplate },
      });
      toast?.success(t('toast.success'), t('campaign.created'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const getColumns = (): DataTableColumnType[] => {
    const columns: DataTableColumnType[] = [
      {
        field: 'name',
        header: account?.isSystem
          ? t('campaign.template.name')
          : t('campaign.name'),
        sortable: true,
        filterable: true,
        frozen: true,
        filters: {
          type: FilterTypeEnum.TEXT,
          placeholder: t('generic.name.search'),
        },
        render: (row: Campaign & CampaignTemplate) => {
          const mt = row.masterTemplate ?? null;
          const displayName =
            account?.isSystem && mt
              ? mt.translations?.find(
                  (tr) => tr.language === row.language && tr.name,
                )?.name ??
                mt.translations?.find(
                  (tr) => tr.language === 'en_GB' && tr.name,
                )?.name ??
                row.name
              : row.name;
          if (
            row.type === CampaignType.WHITELISTING_TEST &&
            account?.isSystem
          ) {
            return (
              <div className="flex align-items-center gap-2">
                {displayName}
                <AppChip
                  label={`${t('campaign.whitelisting.test')}${
                    row.sendingProfile?.type
                      ? ` (${row.sendingProfile.type})`
                      : ''
                  }`}
                  type="purple"
                  size="xs"
                />
              </div>
            );
          }
          return displayName;
        },
      },
    ];

    if (account?.isSystem) {
      columns.push({
        field: 'language',
        header: t('generic.lang'),
        sortable: true,
        filterable: false,
        render: (row: CampaignTemplate) => {
          if (!row.language) return '—';
          return languageValueToKey[row.language] ?? row.language;
        },
      });

      columns.push({
        field: 'masterTemplate',
        header: t('simulationTemplates.masterTemplate'),
        sortable: false,
        filterable: false,
        render: (row: CampaignTemplate) => {
          const mt = row.masterTemplate;
          if (!mt) return null;
          const name =
            mt.translations?.find((tr) => tr.language === 'en_GB' && tr.name)
              ?.name ?? mt.id;
          return (
            <FlexContainer
              justify="flex-start"
              align="center"
              gap="var(--xsmall-padding)"
            >
              <StyledLink
                href={RedirectPaths[
                  RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES_EDIT
                ](mt.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigate(
                    RedirectPaths[
                      RedirectPathsEnum.CAMPAIGN_MASTER_TEMPLATES_EDIT
                    ](mt.id),
                  );
                }}
              >
                {name}
              </StyledLink>
              <AppChip
                label={
                  mt.active
                    ? t('simulationTemplates.status.live')
                    : t('simulationTemplates.status.draft')
                }
                type={mt.active ? 'primary' : 'secondary'}
                size="s"
              />
            </FlexContainer>
          );
        },
      });

      columns.push({
        field: 'isPublished',
        header: t('generic.published'),
        sortable: true,
        filterable: true,
        filters: {
          type: FilterTypeEnum.SELECT_YES_NO,
          field: FilterNamesEnum.CAMPAIGN_TEMPLATE,
        },
        render: (row: CampaignTemplate) =>
          row.isPublished ? (
            <AppChip label={t('dialog.yes')} type="primary" size="s" />
          ) : (
            <AppChip label={t('dialog.no')} type="secondary" size="s" />
          ),
      });
    }

    if (user?.account.isSystem) {
      columns.push(
        {
          field: 'benchmark',
          header: t('campaign.template.phishingType'),
          sortable: true,
          filterable: true,
          filters: {
            field: FilterNamesEnum.CAMPAIGN_BENCHMARK,
            type: FilterTypeEnum.TEXT,
          },
          render: (row: Campaign) =>
            row?.benchmark ? (
              row.benchmark?.name
            ) : (
              <div className="group-row">&#8212;</div>
            ),
        },
        {
          field: 'meta.phishingSign',
          header: t('campaign.template.phishingSign'),
          sortable: true,
          filterable: true,
          filters: {
            field: FilterNamesEnum.CAMPAIGN_PHISHING_SIGN,
            type: FilterTypeEnum.SELECT,
            options: getPhishingSignOptions(t),
          },
          render: (row: Campaign) =>
            row?.meta?.phishingSign ? (
              t(`campaign.template.${row.meta.phishingSign}`)
            ) : (
              <div className="group-row">&#8212;</div>
            ),
        },
      );
    }

    if (!account?.isSystem) {
      if (showIndividualCampaigns) {
        columns.push({
          field: 'targets.users[0].name',
          header: t('campaign.targetName'),
          sortable: false,
          filterable: false,
          render: (row: Campaign) =>
            row.targets?.users?.[0]?.name ? (
              row.targets.users[0].name
            ) : (
              <div className="group-row">&#8212;</div>
            ),
        });
      }
      columns.push(
        {
          field: 'cloneOfName',
          header: t('campaign.template.name'),
          sortable: false,
          filterable: true,
          filters: {
            field: FilterNamesEnum.CAMPAIGN_TEMPLATE_CLONEOF_NAME,
            type: FilterTypeEnum.TEXT,
          },
          render: (row: Campaign) =>
            row.meta?.cloneOfName ? (
              row.meta.cloneOfName
            ) : (
              <div className="group-row">&#8212;</div>
            ),
        },
        ...(can(Actions.READ, Subjects.ACCOUNTS)
          ? [
              {
                field: 'isAutoPhishing',
                header: t('campaign.auto.phishing'),
                sortable: false,
                filterable: true,
                filters: {
                  type: FilterTypeEnum.SELECT_YES_NO,
                },
                render: (row: Campaign) =>
                  row.isAutoPhishing ? (
                    <AppChip label={t('dialog.yes')} type="primary" size="s" />
                  ) : (
                    <AppChip label={t('dialog.no')} type="secondary" size="s" />
                  ),
              },
            ]
          : []),
        {
          field: 'status',
          header: t('generic.status'),
          sortable: true,
          filterable: true,
          filters: {
            field: FilterNamesEnum.CAMPAIGNS_BY_STATUS,
            type: FilterTypeEnum.MULTI_SELECT,
            placeholder: t('generic.status.search'),
            options: getCampaignStatusOptions(t),
          },
          render: (row: Campaign) =>
            getStatus(
              row.status,
              !!row?.useDynamicTarget &&
                row.status === CampaignStatusesEnum.SCHEDULED,
              t,
            ),
        },
        {
          field: 'launchDate',
          header: t('campaign.launchDate'),
          sortable: true,
          filterable: false,
          render: (row: Campaign) => (
            <FlexContainer minWidth="max-content" justify="flex-start">
              {row?.launchDate ? (
                <FormatDate
                  date={row?.launchDate}
                  format={DateFormats.TIMEDATE}
                />
              ) : (
                t('campaing.no.LaunchDate')
              )}
            </FlexContainer>
          ),
        },
        {
          field: 'sendByDate',
          header: t('campaign.sendEndTime'),
          sortable: false,
          filterable: false,
          render: (row: Campaign) => (
            <FlexContainer minWidth="max-content" justify="flex-start">
              {row?.sendByDate ? (
                <FormatDate
                  date={row?.sendByDate}
                  format={DateFormats.TIMEDATE}
                />
              ) : (
                '—'
              )}
            </FlexContainer>
          ),
        },
        {
          field: 'completedDate',
          header: t('campaign.completionDate'),
          sortable: false,
          filterable: false,
          render: (row: Campaign) => (
            <FlexContainer minWidth="max-content" justify="flex-start">
              {row?.completedDate || row?.completeAfter ? (
                <FormatDate
                  date={row?.completedDate || row?.completeAfter}
                  format={DateFormats.TIMEDATE}
                />
              ) : (
                t('campaing.no.completionDate')
              )}
            </FlexContainer>
          ),
        },
        {
          field: 'summary.total',
          headerAriaLabel: t('users'),
          header: (
            <Tooltip
              position="top"
              target="th.users-icon"
              content={t('users')}
            />
          ),
          iconLabel: t('users'),
          headerClassName: 'users-icon',
          sortable: true,
          filterable: true,
          filters: {
            type: FilterTypeEnum.SELECT_YES_NO,
            field: FilterNamesEnum.CAMPAIGN_DYNAMIC_TARGET,
            label: t('campaign.dynamicTarget'),
            placeholder: t('generic.select'),
          },
          render: (row: Campaign) =>
            row?.summary?.total ? row.summary.total : 0,
        },
        {
          field: 'summary.emailsSent',
          headerAriaLabel: t('campaign.emailSent'),
          header: (
            <Tooltip
              position="top"
              target="th.email-sent-icon"
              content={t('campaign.emailSent')}
            />
          ),
          iconLabel: t('campaign.emailSent'),
          headerClassName: 'email-sent-icon',
          sortable: true,
          filterable: false,
          render: (row: Campaign) =>
            row?.summary?.emailsSent ? row.summary.emailsSent : 0,
        },
      );

      if (canUseFeature(Subjects.EMAIL_OPEN_EVENTS)) {
        columns.push({
          field: 'summary.emailsOpen',
          headerAriaLabel: t('campaign.emailOpened'),
          header: (
            <Tooltip
              position="top"
              target="th.email-opened-icon"
              content={t('campaign.emailOpened')}
            />
          ),
          iconLabel: t('campaign.emailOpened'),
          headerClassName: 'email-opened-icon',
          sortable: true,
          filterable: false,
          render: (row: Campaign) =>
            row?.summary?.emailsOpen ? row.summary.emailsOpen : 0,
        });
      }

      columns.push(
        {
          field: 'summary.clickedLinks',
          headerAriaLabel: t('campaign.clicked'),
          header: (
            <Tooltip
              position="top"
              target="th.clicked-link-icon"
              content={t('campaign.clicked')}
            />
          ),
          iconLabel: t('campaign.clicked'),
          headerClassName: 'clicked-link-icon',
          sortable: true,
          filterable: false,
          render: (row: Campaign) =>
            row?.summary?.clickedLinks ? row.summary.clickedLinks : 0,
        },
        {
          field: 'summary.submittedData',
          headerAriaLabel: t('campaign.submitted'),
          header: (
            <Tooltip
              position="top"
              target="th.submitted-data-icon"
              content={t('campaign.submitted')}
            />
          ),
          iconLabel: t('campaign.submitted'),
          headerClassName: 'submitted-data-icon',
          sortable: true,
          filterable: false,
          render: (row: Campaign) =>
            row?.summary?.submittedData ? row.summary.submittedData : 0,
        },
      );

      if (can(Actions.READ, Subjects.FALSE_POSITIVE_IPS)) {
        columns.push({
          field: 'summary.hasFalsePositives',
          headerAriaLabel: t('campaign.falsePositives'),
          header: (
            <>
              <Tooltip
                position="top"
                target="th.false-positive-icon"
                content={t('campaign.falsePositives')}
              />
              <FalsePositiveIcon />
            </>
          ),
          iconLabel: t('campaign.falsePositives'),
          headerClassName: 'false-positive-icon',
          sortable: true,
          filterable: false,
          style: {
            width: '80px',
            textAlign: 'center' as const,
          },
          render: (row: Campaign) => (
            <span
              style={{
                display: 'inline-block',
                padding: 'var(--xxsmall-padding) var(--small-padding)',
                borderRadius: 'var(--grand-border-radius)',
                backgroundColor: 'var(--platinum-main)',
                color: 'var(--gray-darkest)',
                fontSize: 'var(--xsmall-font-size)',
                lineHeight: 'var(--xsmall-line-height)',
              }}
            >
              {row?.summary?.hasFalsePositives
                ? t('campaign.falsePositives.yes')
                : t('campaign.falsePositives.no')}
            </span>
          ),
        });
      }

      if (canUseFeature(Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING)) {
        columns.push({
          field: 'summary.emailsReported',
          headerAriaLabel: t('campaign.email.reported'),
          header: (
            <Tooltip
              position="top"
              target="th.email-reported-icon"
              content={t('campaign.email.reported')}
            />
          ),
          iconLabel: t('campaign.email.reported'),
          headerClassName: 'email-reported-icon',
          sortable: true,
          filterable: false,
          render: (row: Campaign) =>
            row?.summary?.emailsReported ? row.summary.emailsReported : 0,
        });
      }
    }

    columns.push({
      field: 'actions',
      header: t('generic.actions'),
      sortable: false,
      filterable: false,
      style: {
        width: '80px',
        textAlign: 'center' as const,
      },
      render: (row: Campaign) => {
        if (deletingCampaignId === row.id) {
          return (
            <FlexContainer>
              <ProgressSpinner style={{ width: '24px', height: '24px' }} />
            </FlexContainer>
          );
        }
        return (
          <DataTableActions
            disabled={menuItems(row).length < 1}
            menuItems={menuItems(row)}
          />
        );
      },
    });

    return columns;
  };

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = getColumns()
    .filter(
      (column) =>
        !['meta.phishingSign', 'benchmark', 'isAutoPhishing'].includes(
          column.field,
        ),
    )
    .map((column) => column.field);
  const alwaysVisibleColumns = ['name', 'actions'];
  //

  const menuItems = (campaign: Campaign) => {
    const menu: MenuItem[] = [];

    if (
      [CampaignStatusesEnum.DRAFT, CampaignStatusesEnum.SCHEDULED].includes(
        campaign.status,
      )
    ) {
      menu.push({
        label: t('camapign.testEmail.send'),
        icon: 'pi pi-envelope',
        command: () =>
          isDemoAccount
            ? undefined
            : setShowSendTestEmailForACampaing(campaign),
      });
    }

    switch (true) {
      case campaign.status === CampaignStatusesEnum.ACTIVE:
        if (
          can(Actions.UPDATE, Subjects.CAMPAIGNS) &&
          can(Actions.READ, Subjects.ACCOUNTS)
        ) {
          menu.push({
            label: t('generic.complete'),
            icon: 'pi pi-chevron-circle-down',
            command: () => {
              if (isDemoAccount) return;

              setDialogData({
                show: true,
                type: DialogType.CONFIRMATION,
                header: t('campaign.complete'),
                message: t('campaign.complete.confirm'),
                onAccept: async () => {
                  setCampaignId(campaign.id);

                  // Wait for a notify connection to establish
                  await new Promise((resolve) => {
                    const intervalId = setInterval(() => {
                      if (isConnected.current) {
                        clearInterval(intervalId);
                        resolve(true);
                      }
                    }, 100);
                  });
                  try {
                    if (isConnected.current) {
                      await campaignAction.updateStatus({
                        campaignId: campaign.id,
                        action: CampaignActionsEnum.COMPLETE,
                      });

                      await refetch();
                      await summaryRefetch();
                    }
                  } catch (e) {
                    handleAxiosError(e as Error | AxiosError, toast);
                  }
                },
              });
            },
          });
        }
        break;
      case campaign.status === CampaignStatusesEnum.DRAFT:
      case campaign.type === CampaignType.TEMPLATE:
        if (
          can(Actions.UPDATE, Subjects.CAMPAIGNS) &&
          can(Actions.READ, Subjects.ACCOUNTS)
        ) {
          menu.push({
            label: t('generic.edit'),
            icon: 'pi pi-pencil',
            command: () => {
              if (isDemoAccount) return;

              navigate(
                RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](campaign.id),
                {
                  state: { isTemplate: !!account?.isSystem },
                },
              );
            },
          });
        }
        break;
      default:
    }

    if (can(Actions.CREATE, Subjects.CAMPAIGNS)) {
      menu.push({
        label: t('generic.copy'),
        icon:
          copyState === LoadingStatuses.LOADING
            ? 'pi pi-spinner'
            : 'pi pi-copy',
        command: async () => {
          if (isDemoAccount) return;

          await handleCopyCampaign(campaign);
        },
      });
    }

    if (
      campaign.status &&
      ![
        CampaignStatusesEnum.DRAFT,
        CampaignStatusesEnum.PENDING_LAUNCH,
        CampaignStatusesEnum.FAILED_LAUNCH,
      ].includes(campaign.status)
    ) {
      menu.push(
        ...[
          {
            label: t('generic.reports'),
            icon: 'pi pi-chart-line',
            command: () => {
              navigate(
                RedirectPaths[RedirectPathsEnum.CAMPAIGNS_REPORT](campaign.id),
              );
            },
          },
        ],
      );
    }

    if (
      campaign.type !== CampaignType.WHITELISTING_TEST &&
      ((can(Actions.DELETE, Subjects.CAMPAIGNS) &&
        can(Actions.READ, Subjects.ACCOUNTS)) ||
        (can(Actions.DELETE, Subjects.CAMPAIGNS) &&
          cannot(Actions.READ, Subjects.ACCOUNTS) &&
          campaign.isAutoPhishing &&
          (campaign.status === CampaignStatusesEnum.DRAFT ||
            campaign.status === CampaignStatusesEnum.SCHEDULED)))
    ) {
      menu.push({
        label: t('generic.delete'),
        icon: 'pi pi-times',
        disabled: campaign.status === CampaignStatusesEnum.PENDING_LAUNCH,
        command: async () => {
          if (isDemoAccount) return;

          setDialogData({
            show: true,
            type: DialogType.CONFIRMATION,
            header: account?.isSystem
              ? t('campaign.template.delete')
              : t('campaign.delete'),
            message: account?.isSystem
              ? t('campaign.template.delete.confirm')
              : t('campaign.delete.confirm'),
            onAccept: async () => {
              if (account?.isSystem) {
                try {
                  await handleDeleteCampaign(campaign.id);
                } catch (e) {
                  handleAxiosError(e as Error | AxiosError, toast);
                } finally {
                  toast?.success(
                    t('toast.success'),
                    t('campaign.template.deleted'),
                  );
                  setDeletingCampaignId(null);
                  await refetchCampaignTemplates();
                }
                return;
              }

              setIdForDeletion(campaign.id);

              // Wait for a notify connection to establish
              await new Promise((resolve) => {
                const intervalId = setInterval(() => {
                  if (isConnectedForDeletion.current) {
                    clearInterval(intervalId);
                    resolve(true);
                  }
                }, 100);
              });
              try {
                if (isConnectedForDeletion.current) {
                  await handleDeleteCampaign(campaign.id);
                }
              } catch (e) {
                handleAxiosError(e as Error | AxiosError, toast);
              }
            },
          });
        },
      });
    }

    return menu?.length ? menu : [];
  };

  const dropdownButtonOptions: MenuItem[] = [
    {
      label: t('campaigns.fromTemplate'),
      icon: 'pi pi-table',
      command: () => setShowCampaignTemplatesModal(true),
    },
    {
      label: t('campaigns.fromScratch'),
      icon: 'pi pi-stop',
      command: () => handleCreateNewCampaign({ isTemplate: false }),
    },
  ];

  const toolbar = (
    <DataTableToolbar justify="space-between">
      <DataTableFilters
        filters={getFiltersFromColumns(getColumns())}
        onFilter={onFilter}
        tableName={
          account?.isSystem
            ? TableNamesEnum.CAMPAIGNS_LIST_SYSTEM
            : TableNamesEnum.CAMPAIGNS_LIST_CUSTOMER
        }
      />
      <FlexContainer gap={12} justify="flex-end" flex="0 1 50%" wrap="wrap">
        {account?.enableAiTrainingForVulnerableUsers && (
          <StyledTabs
            options={[
              t(`user.campaign.type.${UserCampaignType.COMPANY_WIDE}`),
              t(`user.campaign.type.${UserCampaignType.INDIVIDUAL}`),
            ]}
            requiredOptions={[]}
            onToggle={(option) =>
              setShowIndividualCampaigns(
                option ===
                  t(`user.campaign.type.${UserCampaignType.INDIVIDUAL}`),
              )
            }
            defaultSelected={
              showIndividualCampaigns
                ? t(`user.campaign.type.${UserCampaignType.INDIVIDUAL}`)
                : t(`user.campaign.type.${UserCampaignType.COMPANY_WIDE}`)
            }
            className="ml-3 font-medium"
          />
        )}
        {can(Actions.CREATE, Subjects.CAMPAIGNS) && (
          <>
            {account?.isSystem ? (
              <AppButton
                label={t('campaign.template.new')}
                severity="secondary"
                onClick={() => handleCreateNewCampaign({ isTemplate: true })}
                state={queryStateConverter(saveCampaign)}
              />
            ) : (
              <AppDropdownButton
                menuId="campaign-create"
                label={t('campaign.create')}
                items={dropdownButtonOptions}
                severity="secondary"
                state={queryStateConverter(saveCampaign)}
              />
            )}
          </>
        )}
        {isTable && (
          <DataTableColumnsMultiselect
            columns={getColumns()}
            tableName={
              account?.isSystem
                ? TableNamesEnum.CAMPAIGNS_LIST_SYSTEM
                : TableNamesEnum.CAMPAIGNS_LIST_CUSTOMER
            }
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
            defaultVisibleColumns={defaultVisibleColumns}
            alwaysVisibleColumns={alwaysVisibleColumns}
          />
        )}
        <AppSelectPreviewType
          tableName="campaignsTable"
          accountId={account?.id}
          userId={user?.id}
          selectedPreviewType={selectedPreviewType}
          setSelectedPreviewType={setSelectedPreviewType}
        />
      </FlexContainer>
    </DataTableToolbar>
  );

  const onRowClick = (campaign: Campaign | CampaignTemplate) => {
    if (areCampaignsLoading || areCampaignTemplatesLoading) return;

    if (isDemoAccount && campaign.status === CampaignStatusesEnum.DRAFT) {
      return;
    }

    if (campaign.type === CampaignType.TEMPLATE && user?.account.isSystem) {
      navigate(
        `${RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](
          campaign.id,
        )}?isTemplate=true`,
        {
          state: { isTemplate: true },
        },
      );
      return;
    }

    const campaignHasReport = ![
      CampaignStatusesEnum.DRAFT,
      CampaignStatusesEnum.PENDING_LAUNCH,
      CampaignStatusesEnum.FAILED_LAUNCH,
    ].includes(campaign.status);

    if (campaignHasReport && !account?.isSystem) {
      navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS_REPORT](campaign.id));
    } else if (user?.account.isSystem) {
      navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](campaign.id), {
        state: { isTemplate: !!account?.isSystem },
      });
    }
  };

  return (
    <>
      <StyledDiv>
        {isTable ? (
          <DataTable
            data={data?.result}
            count={data?.count as number}
            isLoading={areCampaignsLoading || areCampaignTemplatesLoading}
            toolbar={toolbar}
            columns={getColumns()}
            visibleColumns={visibleColumns}
            rows={take}
            skip={skip}
            onPage={onPage}
            onSort={onSort}
            sort={sort}
            onRowClick={(event: DataTableRowClickEvent) =>
              onRowClick(event.data as Campaign)
            }
            stickyScrollbar
          />
        ) : (
          <GridTable
            count={data?.count}
            take={take}
            skip={skip}
            toolbarContent={toolbar}
            onPageChange={onGridPageChange}
            onRowsChange={onGridRowChange}
            isLoading={areCampaignsLoading || areCampaignTemplatesLoading}
            rowsPerPage={[8, 16]}
          >
            {!areCampaignsLoading &&
              !areCampaignsLoading &&
              !!data?.result.length && (
                <>
                  <ThumbnailsGenerationNotify
                    refetch={refetch}
                    templates={data?.result.map(
                      (campaign: Campaign) => campaign.emailTemplate,
                    )}
                  />
                  <StyledCardGridViewContainer>
                    {data?.result.map(
                      (campaign: Campaign | CampaignTemplate) => (
                        <ImageCard
                          key={campaign.id}
                          id={campaign.id}
                          title={campaign.name}
                          status={
                            !account?.isSystem
                              ? displayCampaignStatus(t, campaign.status)
                              : undefined
                          }
                          author={displayCreatedBy(t, campaign.createdBy)}
                          thumbnail={
                            campaign.emailTemplate?.thumbnail?.signedUrl
                          }
                          templateType="email"
                          interactionType="withActions"
                          disabledActions={menuItems(campaign).length < 1}
                          actions={menuItems(campaign)}
                          isTemplate={
                            account?.isSystem
                              ? (campaign as CampaignTemplate).isPublished
                              : false
                          }
                          onClick={() =>
                            onRowClick(campaign as Campaign | CampaignTemplate)
                          }
                        />
                      ),
                    )}
                  </StyledCardGridViewContainer>
                </>
              )}
          </GridTable>
        )}
      </StyledDiv>
      <SendTestEmailModal
        email={user?.email as string}
        visible={!!showSendTestEmailForACampaing}
        onSubmit={handleSendTestEmail}
        onHide={() => setShowSendTestEmailForACampaing(null)}
        extended={false}
        withName
      />
      <CampaignTemplatesModal
        visible={showCampaignTemplatesModal}
        onHide={() => setShowCampaignTemplatesModal(false)}
      />
    </>
  );
};
